import {
  midiNotePlayed,
  midiNoteStopped,
  onMidiDeviceStateChange,
  onMidiFootPedal_pressed,
  onMidiFootPedal_released
} from "../eventBus/midi/midiEventBus";
import {logger} from "../log/logger";
import nativeBridge from "../nativeBridge";
import {NormalizedMidiInputArray} from "../../models/NormalizedMidiInputArray";
import {NativeMidiInputArray} from "../../models/NativeMidiInputArray";
import {settingsStore} from "../../redux/settings/settingsStore";
import {deviceStore} from "../../redux/device/deviceStore";
import {NormalizedMidiInput} from "../../models/NormalizedMidiInput";
import {NativeMidiInput} from "../../models/NativeMidiInput";
import debounce from "lodash/debounce";
import {subscribeToReceiveMessagesFromNativeApp} from "../eventBus/nativeApp/receiveMessageFromNativeAppEventBus";
import {
  FromNativeAppEventTypes,
  INativeMidiControlChange,
  INativeMidiDevicesChanged,
  INativeMidiNoteOff,
  INativeMidiNoteOn
} from "../eventBus/nativeApp/FromNativeAppEventTypes";
import { legacyMidiBridge } from "./LegacyMidiBridge";
import { isIgnoredMidiDevice } from "./midiDeviceFilters";

type MIDIMessageEvent = WebMidi.MIDIMessageEvent;

class MidiService {
  #normalizedMidiInputs: NormalizedMidiInputArray | null = null;
  readonly #debouncedListenToAllMidiInputs = debounce(this.#listenToAllMidiInputs, 100);
  #midiAccess: WebMidi.MIDIAccess | null = null;

  // Native MIDI (iOS MIDIKit bridge)
  #nativeMidiInputs: Map<number, NativeMidiInput> = new Map();
  #nativeMidiSubscription: (() => void) | null = null;
  #previousNativeMidiDeviceIds: Set<number> = new Set();

  constructor() {
  }

  /**
   * Initialize native MIDI listening for platforms without WebMIDI API (iOS).
   * 
   * Supports both:
   * - New native apps (MIDIKit) that send events via nativeBridge
   * - Old native apps (IosWebMidi) that inject JavaScript callbacks
   * 
   * @see documentation/midi/midi-backward-compatibility-plan.md
   */
  initializeNativeMidi(): void {
    // Guard: Prevent duplicate initialization (e.g., React 18 Strict Mode, component remounts)
    // Without this check, each call would create a new subscription while the old one remains active,
    // causing duplicate MIDI event processing.
    if (this.#nativeMidiSubscription !== null) {
      logger.midiAccess.info({ message: "Native MIDI: Already initialized, skipping" });
      return;
    }

    logger.midiAccess.info({ message: "Native MIDI: Initializing native bridge" });

    // Initialize legacy bridge for backward compatibility with old native apps.
    // This registers global _callback_* functions that old native (IosWebMidi.m) 
    // injects JavaScript to call. New native (MIDIKit) doesn't use these callbacks.
    legacyMidiBridge.initializeLegacyMidiBridge();

    // Subscribe to native MIDI events from new native (MIDIKit bridge)
    // Old native doesn't send events through this channel - it uses JS injection instead.
    this.#nativeMidiSubscription = subscribeToReceiveMessagesFromNativeApp((message: FromNativeAppEventTypes) => {
      this.#handleNativeMidiEvent(message);
    });

    // Send BOTH init messages - each native version responds to one, ignores the other.
    // This allows the same web code to work with both old and new native apps.
    
    // 1. Legacy init message for old native (IosWebMidi.m)
    // Old native listens for this and responds via _callback_onReady() JavaScript injection.
    // New native ignores this message.
    logger.midiAccess.info({ message: "Native MIDI: Sending legacy webmidipolyfill.onready for backward compatibility" });
    nativeBridge.sendMessageToNative({
      type: "webmidipolyfill.onready",
      param: {
        options: { sysex: false },
        url: window.location.href,
      },
    });

    // 2. New init message for new native (MIDIKit bridge)
    // New native listens for this and responds via midi.devicesChanged through nativeBridge.
    // Old native ignores this message.
    logger.midiAccess.info({ message: "Native MIDI: Requesting initial device list via midi.getDevices_requested" });
    settingsStore.setIsMidiDeviceOptionsLoading(true);
    nativeBridge.sendMessageToNative({ type: "midi.getDevices_requested" });
  }

  #handleNativeMidiEvent(message: FromNativeAppEventTypes): void {
    switch (message.type) {
      case "midi.noteOn":
        // Note: a Note On of velocity 0 means a key release under MIDI 1.0 but a genuine key
        // press under MIDI 2.0, and this layer cannot tell which protocol produced the event —
        // the bridge flattens every velocity to a 7-bit MIDI 1.0 value before sending it. So the
        // translation is done in MidiKitBridge.swift, where the protocol is still known, and is
        // deliberately not repeated here.
        this.#onNativeMidiNoteOn(message);
        break;
      case "midi.noteOff":
        this.#onNativeMidiNoteOff(message);
        break;
      case "midi.controlChange":
        this.#onNativeMidiControlChange(message);
        break;
      case "midi.devicesChanged":
        this.#onNativeMidiDevicesChanged(message);
        break;
    }
  }

  #onNativeMidiNoteOn(event: INativeMidiNoteOn): void {
    // Look up the NativeMidiInput by the device ID from the event
    const input = this.#getNativeInputByDeviceId(event.deviceId);
    
    // Auto-select device if no device is currently selected
    if (!this.#isSelectedMidiDeviceConnectedNative()) {
      logger.midiNoteAction.info({
        message: `Native MIDI: Selected device not connected, auto-selecting: { id: ${input.getId()}, name: ${input.getName()} }`,
      });
      settingsStore.setSelectedMidiDevice(input.getId(), input.getName());
    }

    logger.midiNoteAction.info({
      message: `Native MIDI note played: { pitch: ${event.pitch}, velocity: ${event.velocity}, deviceId: ${event.deviceId} }`
    });

    midiNotePlayed(
      event.pitch,
      event.velocity,
      input.getId(),
      event.channel,
      event.timestamp,
      input.getId(),
      input,
      input.getName()
    );

    // Note: We don't send midi.note_played back to native here because:
    // 1. iOS uses WebView audio, not native audio
    // 2. Native already has the MIDI data (it sent it to us)
  }

  #onNativeMidiNoteOff(event: INativeMidiNoteOff): void {
    // Look up the NativeMidiInput by the device ID from the event
    const input = this.#getNativeInputByDeviceId(event.deviceId);
    
    midiNoteStopped(
      event.pitch,
      input.getId(),
      event.channel,
      event.timestamp,
      input.getId(),
      input,
      input.getName()
    );

    // Note: We don't send midi.note_stopped back to native here because:
    // 1. iOS uses WebView audio, not native audio
    // 2. Native already has the MIDI data (it sent it to us)
  }

  #onNativeMidiControlChange(event: INativeMidiControlChange): void {
    // Look up the NativeMidiInput by the device ID from the event
    const input = this.#getNativeInputByDeviceId(event.deviceId);
    
    // Controller 64 is the sustain pedal
    const isSustainPedal = event.controller === 64;
    const isPressed = event.value > 0;

    if (isSustainPedal) {
      if (isPressed) {
        onMidiFootPedal_pressed(event.controller, input.getId(), event.channel, event.timestamp, input.getId(), input.getName());
      } else {
        onMidiFootPedal_released(event.controller, input.getId(), event.channel, event.timestamp, input.getId(), input.getName());
      }
    }
  }

  #onNativeMidiDevicesChanged(event: INativeMidiDevicesChanged): void {
    logger.midiConnectionEvent.info({
      message: `Native MIDI devices changed: ${event.devices.length} device(s)`
    });

    // Update our map of native inputs
    this.#nativeMidiInputs.clear();
    const nativeInputArray = new NativeMidiInputArray();
    const currentDeviceIds = new Set<number>();
    
    for (const device of event.devices) {
      // Filter out network sessions - these are iOS artifacts that we don't want to expose to users
      if (isIgnoredMidiDevice(device.name)) {
        logger.midiAccess.info({
          message: `Native MIDI: Ignoring network session device "${device.name}"`
        });
        continue;
      }
      
      const nativeInput = new NativeMidiInput(device);
      this.#nativeMidiInputs.set(device.id, nativeInput);
      nativeInputArray.push(nativeInput);
      currentDeviceIds.add(device.id);
    }

    // Update settings store with connection status and device options
    settingsStore.setIsMidiDeviceConnected(nativeInputArray.length > 0);
    settingsStore.setMidiDeviceOptions(nativeInputArray);
    settingsStore.setIsMidiDeviceOptionsLoading(false);

    // Find devices that were added (in current but not in previous)
    const addedDevices: NativeMidiInput[] = [];
    for (const deviceId of currentDeviceIds) {
      if (!this.#previousNativeMidiDeviceIds.has(deviceId)) {
        const device = this.#nativeMidiInputs.get(deviceId);
        if (device) {
          addedDevices.push(device);
        }
      }
    }

    // Find if any devices were removed (in previous but not in current)
    let deviceWasRemoved = false;
    for (const deviceId of this.#previousNativeMidiDeviceIds) {
      if (!currentDeviceIds.has(deviceId)) {
        deviceWasRemoved = true;
        break;
      }
    }

    // Update previous device IDs for next comparison
    this.#previousNativeMidiDeviceIds = currentDeviceIds;

    // Notify listeners of the change
    // Call onMidiDeviceStateChange for each newly-added device so the lesson service
    // can properly determine if each added device is configured or unconfigured
    if (addedDevices.length > 0) {
      for (const device of addedDevices) {
        onMidiDeviceStateChange(device);
      }
    } else if (deviceWasRemoved) {
      // Device was unplugged - pass undefined to trigger unplug handling
      onMidiDeviceStateChange(undefined);
    }
    // Note: If no devices were added and none were removed, the device list is unchanged.
    // We intentionally do NOT call onMidiDeviceStateChange in this case to avoid
    // incorrectly triggering the "device plugged in" flow in lessonService.
  }

  /**
   * Look up a NativeMidiInput by its device ID.
   * Falls back to creating a placeholder if the device isn't in our map yet
   * (e.g., if we receive a note event before the device list arrives).
   */
  #getNativeInputByDeviceId(deviceId: number): NativeMidiInput {
    // Try to find the device in our map
    const input = this.#nativeMidiInputs.get(deviceId);
    if (input) {
      return input;
    }
    
    // Device not in map yet - create a placeholder with the correct ID
    // This can happen if we receive a note event before the devicesChanged event
    logger.midiAccess.info({
      message: `Native MIDI: Device ${deviceId} not in map, creating placeholder`
    });
    
    const placeholder = new NativeMidiInput({
      id: deviceId,
      name: "MIDI Keyboard",
      manufacturer: "Unknown"
    });
    
    // Store it so subsequent events from this device use the same instance
    this.#nativeMidiInputs.set(deviceId, placeholder);
    
    return placeholder;
  }

  hasMidiAccess(): boolean {
    return this.#midiAccess !== null;
  }

  #setNormalizedInputsFromMidiAccess(): void {
    logger.midiInputs.info({ message: "  Normalizing midi inputs" });

    // Guard: Make sure midiAccess is available before proceeding
    const midiAccess = this.#midiAccess;
    if (!midiAccess) {
      logger.midiAccess.error({ message: "  MIDI access is not available." });
      throw new Error("MIDI access is not available.");
    }

    // If we have already normalized the inputs, return
    if (this.#normalizedMidiInputs !== null) {
      logger.midiInputs.info({ message: "  Inputs have already been normalized. Returning memoized inputs" });
      return;
    }

    const result: NormalizedMidiInputArray = new NormalizedMidiInputArray();

    // names inside midiAccess.inputs are readonly. Need to copy the inputs so we can modify the names.
    const workingCopy: { name: string | undefined, midiInput: WebMidi.MIDIInput }[] = [];

    // I tried many different ways to loop through midiAccess.inputs.values(). This was the only approach I could get
    // to work on iOS. Can't use normal iteration here because on iOS the "iterator" returned from midiAccess.inputs.values() is
    // not a normal iterator and doesn't implement the required methods / properties of an iterable object.
    let values = midiAccess.inputs.values();
    let next = values.next();
    while (!next.done) {
      workingCopy.push({
        name: next.value.name?.toString(),
        midiInput: next.value
      });
      next = values.next();
    }

    const usedNames: string[] = [];
    const isAndroid = deviceStore.getDeviceOsIsAndroid();

    for (const input of workingCopy) {
      // Filter the inputs to exclude the network session and session inputs
      if (isIgnoredMidiDevice(input.name ?? "")) { continue; }

      // Make sure names are unique
      let finalName = input.name === undefined ? `Unknown` : input.name;
      let counter = 2;
      while (usedNames.includes(finalName)) {
        finalName = `${input.name} (${counter})`;
        counter++;
      }
      usedNames.push(finalName);

      result.push(new NormalizedMidiInput(input.midiInput, finalName, isAndroid));
    }

    logger.midiInputs.info({
      message: `Found ${result.length} midi inputs: ${result.toString()}`,
    });

    this.#normalizedMidiInputs = result;

    this.#onInputsChanged();
  }

  #onInputsChanged(): void {
    const newInputs = this.#normalizedMidiInputs ?? new NormalizedMidiInputArray();
    settingsStore.setIsMidiDeviceConnected(newInputs.length > 0);
    settingsStore.setMidiDeviceOptions(newInputs);
  }

  #listenToAllMidiInputs() {
    logger.midiInputs.info({
      message: "Updating devices being listened to"
    });

    const inputs = this.#normalizedMidiInputs ?? new NormalizedMidiInputArray();
    const newInputs = new NormalizedMidiInputArray();

    for(const normalizedMidiInput of inputs) {
      normalizedMidiInput.getMidiInput().onmidimessage = (e: MIDIMessageEvent)=> {
        this.#onMidiMessage_received(normalizedMidiInput, e);
      };
      newInputs.push(normalizedMidiInput);
    }

    this.#normalizedMidiInputs = newInputs;

    logger.midiInputs.info({
      message: `"Subscribed to ${newInputs.length} inputs: ${newInputs.toString()}`
    });
  }

  #unsubscribeToMessagesFromAllMidiInputDevices(): void {
    logger.midiInputs.info({
      message: `  Unsubscribing from ${this.#normalizedMidiInputs?.length ?? 0} midi inputs. ${this.#normalizedMidiInputs?.toString() ?? ""}`
    });

    for(const input of this.#normalizedMidiInputs ?? []) {
      input.getMidiInput().onmidimessage = (_e: MIDIMessageEvent) => {};
    }

    // Clear the array
    this.#normalizedMidiInputs = new NormalizedMidiInputArray();

    // Set to null so that we know we need to re-fetch the inputs next time
    this.#normalizedMidiInputs = null;
  }

  /**
   * Responsible for calling window.navigator.requestMIDIAccess() on the webview. This is the only code in our codebase
   * that should directly call window.navigator.requestMIDIAccess().
   */
  async requestMidiAccess(): Promise<void> {
    logger.midiAccess.log({message: "Requesting - begin"});

    // Note: This method is only called on Android/Electron (iOS uses native MIDIKit bridge)
    return new Promise((resolve, reject): void => {
      // If we already have midi access, resolve immediately
      if (this.#midiAccess) {
        resolve();
        return;
      }

      // If the document is not ready, the promise which results from calling window.navigator.requestMIDIAccess may never be resolved or rejected.
      // To avoid this, we wait for the document to load before calling window.navigator.requestMIDIAccess.
      if (document?.readyState !== 'complete') {
        logger.midiAccess.log({message: "Document is not ready. Deferring MIDI access until after the 'load' event"})
        return window.addEventListener('load', () => {
          this.requestMidiAccess()
            .then((midiAccess) => {
              logger.midiAccess.log({message: "Got MIDI access after the document was loaded."});
              return resolve(midiAccess);
            })
            .catch((reason) => {
              logger.midiAccess.log({
                message: "Failed to get MIDI access after the document was loaded",
                error: reason instanceof Error ? reason : new Error(String(reason)),
              });
              return reject(reason);
            });
        });
      }

      // Note: This code path is only executed on Android/Electron (iOS uses native MIDIKit bridge)
      logger.midiAccess.log({message: "Requesting WebMIDI access via native API"});
      settingsStore.setIsMidiDeviceOptionsLoading(true);

      // Timeout to handle Chromium bug where requestMIDIAccess() can hang indefinitely.
      // This ensures the retry logic in MIDIConnection.tsx can kick in (5 retries, 1 second apart).
      const MIDI_ACCESS_TIMEOUT_MS = 1000;
      let didSettle = false;

      const timeoutId = window.setTimeout(() => {
        if (!didSettle) {
          didSettle = true;
          settingsStore.setIsMidiDeviceOptionsLoading(false);
          logger.midiAccess.log({message: "Timeout - WebMIDI API did not respond"});
          reject(new Error("WebMIDI access request timed out. The browser's MIDI API did not respond."));
        }
      }, MIDI_ACCESS_TIMEOUT_MS);

      window.navigator.requestMIDIAccess()
        .then((midiAccess: WebMidi.MIDIAccess) => {
          if (didSettle) {
            // Timeout already fired, ignore this late response
            return;
          }
          didSettle = true;
          clearTimeout(timeoutId);

          logger.midiAccess.log({message: "Acquired"});

          // onMidiAccessState_changed will get called once for each input that is connected.
          midiAccess.onstatechange = (event: WebMidi.MIDIConnectionEvent) => {
            // Guard: at this point in time we only care about inputs
            if (event.port && event.port.type && event.port.type !== "input") {
              return;
            }

            this.onMidiAccessState_changed(event);
          };

          this.#midiAccess = midiAccess;
          this.#onGetMidiAccessCalled();
          settingsStore.setIsMidiDeviceOptionsLoading(false);
          resolve();
        })
        .catch((e) => {
          if (didSettle) {
            // Timeout already fired, ignore this late rejection
            return;
          }
          didSettle = true;
          clearTimeout(timeoutId);
          settingsStore.setIsMidiDeviceOptionsLoading(false);

          logger.midiAccess.log({message: "Rejected"});
          if (e.message.includes("The request is not allowed by the user agent or the platform in the current context")) {
            const error = new Error("MIDI access request was blocked for this user or application. Please enable midi access for the application and try again.");
            reject(error);
            return;
          }

          reject(e);
        });
    });
  }

  onMidiAccessState_changed(event: WebMidi.MIDIConnectionEvent) {
    const message = event.port ? `Midi device { id: ${event.port.id}, name: ${event.port.name} } was ${event.port.state === "connected" ? "plugged in" : "unplugged"}.` : `Midi device information not available`;
    logger.midiConnectionEvent.info({ message: message });

    const doesPortIdMatch = (input: NormalizedMidiInput) => input.getMidiInput().id === (event.port?.id ?? "");

    const unpluggedInput = this.#normalizedMidiInputs?.find(doesPortIdMatch);

    this.#updateMidiInputs();

    const newlyPluggedInInput = this.#normalizedMidiInputs?.find(doesPortIdMatch);

    const normalizedInputThatChanged = newlyPluggedInInput ?? unpluggedInput;

    onMidiDeviceStateChange(normalizedInputThatChanged);
  }

  #updateMidiInputs(): void {
    logger.midiInputs.info({
      message: `Updating midi inputs and listeners - begin`
    });

    this.#unsubscribeToMessagesFromAllMidiInputDevices();
    this.#setNormalizedInputsFromMidiAccess();
    this.#debouncedListenToAllMidiInputs();
  }

  #onGetMidiAccessCalled(): void {
    logger.midiInputs.info({ message: `onGetMidiAccessCalled` });
    this.#updateMidiInputs();
  }

  #onMidiMessage_received(input: NormalizedMidiInput, e: MIDIMessageEvent){
    const [command, number, velocity = 0] = e.data; //noteoff won't have velocity
    const noteOnShift = 9;
    const noteOffShift = 8;
    const commandShifted = command >> 4;
    const channel = (command & 0xf) + 1;
    const isNoteOn = commandShifted === noteOnShift && velocity !== 0;
    const isNoteOff = (commandShifted === noteOffShift ) || (commandShifted === noteOnShift && velocity === 0);
    const isFootPedalOn = number === 64 && commandShifted === 11 && velocity > 0;
    const isFootPedalOff = number === 64 && commandShifted === 11 && velocity === 0;

    if(isFootPedalOn) {
      onMidiFootPedal_pressed(number, input.getId(), channel, e.timeStamp, input.getId(), input.getName());
    } else if(isFootPedalOff) {
      onMidiFootPedal_released(number, input.getId(), channel, e.timeStamp, input.getId(), input.getName());
    }

    if(isNoteOn) {
      this.#onMidiNote_played(number, velocity, input, channel, e.timeStamp);
    } else if(isNoteOff) {
      this.#onMidiNote_stopped(number, velocity, input, channel, e.timeStamp);
    }
  }

  #onMidiNote_played(number: number, velocity: number, input: NormalizedMidiInput, channel: number, timestamp: number) {
    const canProceed = this.#onBeforeMidiNote_played(number, velocity, input, channel, timestamp);
    if (!canProceed) {
      return;
    }

    midiNotePlayed(number, velocity, input.getId(), channel, timestamp, input.getId(), input, input.getName());

    logger.midiNoteAction.info({
      message: `note played: { pitch: ${number}, velocity: ${velocity}}`
    });

    this.#onAfterMidiNote_played(number, velocity, input, channel, timestamp);
  }

  #onBeforeMidiNote_played(_number: number, _velocity: number, input: NormalizedMidiInput, _channel: number, _timestamp: number): boolean {
    // If the user is sending midi input, and the selected midi device in not connected, then update the selected device to be the one that is sending input.
    if (!this.#isSelectedMidiDeviceConnected()) {
      logger.midiNoteAction.info({
        message: `Selected midi device is not connected, updating selected midi device to the one that is sending input: { id: ${input.getId()}, name: ${input.getName()} }`,
      });
      settingsStore.setSelectedMidiDevice(input.getId(), input.getName());
    }
    return true;
  }

  #onAfterMidiNote_played(number: number, velocity: number, _input: NormalizedMidiInput, _channel: number, _timestamp: number): void {
    // Send to native for Android native audio playback
    // (Android WebView audio has issues, so we use native audio)
    nativeBridge.sendMessageToNative({type: "midi.note_played", timestamp: Date.now(), pitch: number, velocity, origin: "webMidi"});
  }

  #isSelectedMidiDeviceConnected(): boolean {
    const selectedDeviceName = settingsStore.getSelectedMidiDeviceName();
    const input = this.#normalizedMidiInputs?.filter((i) => i.getName() === selectedDeviceName)[0] ?? null;
    return !!input;
  }

  #isSelectedMidiDeviceConnectedNative(): boolean {
    const selectedDeviceId = settingsStore.getSelectedMidiDeviceId();
    return this.#nativeMidiInputs.has(Number(selectedDeviceId));
  }

  #onMidiNote_stopped(number: number, _velocity: number, input: NormalizedMidiInput, channel: number, timestamp: number) {
    midiNoteStopped(number, input.getId(), channel, timestamp, input.getId(), input, input.getName());
    // Send to native for Android native audio playback
    nativeBridge.sendMessageToNative({type: "midi.note_stopped", timestamp: Date.now(), pitch: number, origin: "webMidi"});
  }
}

const midi = new MidiService();
export default midi;
